import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { usePresence } from '../hooks/usePresence'
import { menuSize, placeMenu, arrowDirectionFromRects } from '../utils/menuPlacement'
import './glassMenu.css'

function buildActions(item, { refreshing, labels, include }) {
  const actions = [
    { id: 'history', label: labels.history, icon: '/Icon query stats.png' },
  ]
  if (item?.link) {
    actions.push({
      id: 'refresh',
      label: refreshing ? labels.refreshing : labels.refresh,
      icon: '/Icon euro symbol.png',
      disabled: refreshing,
    })
    actions.push({ id: 'open', label: labels.open, icon: '/Icon chrome.png' })
  }
  actions.push({ id: 'edit', label: labels.edit, icon: '/Icon drive file rename outline.png' })
  actions.push({ id: 'saved', label: labels.saved, icon: '/Icon inventory.png' })
  actions.push({ id: 'delete', label: labels.delete, icon: '/Icon trash.png', danger: true })
  if (!include?.length) return actions
  return actions.filter((action) => include.includes(action.id))
}

export default function GridItemActionsMenu({
  isOpen = false,
  anchor = null,
  item = null,
  refreshing = false,
  labels = {},
  include,
  onClose,
  onAction,
  onPlacementChange,
}) {
  const menuRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const { mounted, leaving } = usePresence(isOpen, { duration: 180, reduced: reducedMotion })
  const resolvedLabels = {
    history: labels.history || 'Historique des prix',
    refresh: labels.refresh || 'Actualiser le prix',
    refreshing: labels.refreshing || 'Actualisation…',
    open: labels.open || 'Ouvrir le lien',
    edit: labels.edit || 'Modifier',
    saved: labels.saved || 'Mettre de côté',
    delete: labels.delete || 'Supprimer',
  }
  const actions = useMemo(
    () => buildActions(item, { refreshing, labels: resolvedLabels, include }),
    [item, refreshing, include, resolvedLabels.history, resolvedLabels.refresh, resolvedLabels.refreshing, resolvedLabels.open, resolvedLabels.edit, resolvedLabels.saved, resolvedLabels.delete],
  )
  const [activeIndex, setActiveIndex] = useState(0)
  const [coords, setCoords] = useState({ left: 0, top: 0, origin: 'top right' })

  useLayoutEffect(() => {
    if (!mounted || !anchor) return undefined
    const update = () => {
      const size = menuSize(menuRef.current)
      const rect = anchor.getBoundingClientRect?.() || anchor
      const x = (rect.right || 0) - size.width
      const y = (rect.bottom || 0) + 6
      const placed = placeMenu({
        x,
        y,
        width: size.width,
        height: size.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
      })
      const originY = placed.top + size.height / 2 < (rect.top || 0) ? 'bottom' : 'top'
      const originX = placed.left + size.width / 2 < (rect.left || 0) + ((rect.width || 0) / 2) ? 'left' : 'right'
      const next = { ...placed, origin: `${originY} ${originX}` }
      const direction = arrowDirectionFromRects(rect, {
        left: placed.left,
        top: placed.top,
        width: size.width,
        height: size.height,
      })
      onPlacementChange?.(direction)
      setCoords((prev) => (
        prev.left === next.left && prev.top === next.top && prev.origin === next.origin
          ? prev
          : next
      ))
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [mounted, anchor, actions.length, refreshing, onPlacementChange])

  useEffect(() => {
    if (!mounted) return undefined
    const node = menuRef.current
    const focusable = node?.querySelectorAll('[role="menuitem"]:not(:disabled)')
    focusable?.[Math.min(activeIndex, (focusable.length || 1) - 1)]?.focus()
  }, [activeIndex, coords, mounted])

  useEffect(() => {
    if (!mounted) return undefined
    const onPointer = (event) => {
      if (menuRef.current?.contains(event.target) || anchor?.contains?.(event.target)) return
      onClose?.()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => (index + 1) % actions.length)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => (index - 1 + actions.length) % actions.length)
      } else if (event.key === 'Home') {
        event.preventDefault()
        setActiveIndex(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        setActiveIndex(actions.length - 1)
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [mounted, actions.length, onClose, anchor])

  useEffect(() => {
    if (isOpen) setActiveIndex(0)
  }, [isOpen, item?.id])

  if (!mounted || typeof document === 'undefined' || !item) return null

  return createPortal(
    <div
      className="glass-menu-root"
      style={{ left: coords.left, top: coords.top }}
      data-testid="grid-item-actions-menu"
    >
      <div className="lg-blur-prewarm" aria-hidden="true" />
      <div
        ref={menuRef}
        className={`lg-panel glass-menu glass-menu--on-cart${leaving ? ' is-leaving' : ''}`}
        role="menu"
        aria-label="Actions du produit"
        style={{ transformOrigin: coords.origin }}
      >
        <div className="lg-panel-frost" />
        <div className="lg-panel-tint" />
        <div className="lg-panel-shine" />
        <ul className={`lg-panel-body glass-menu-list glass-menu-motion${reducedMotion ? '' : leaving ? ' is-leaving' : ' is-animated'}`}>
          {actions.map((action, index) => (
            <li key={action.id} role="none">
              <button
                type="button"
                role="menuitem"
                className={`glass-menu-item${action.danger ? ' is-danger' : ''}${index === activeIndex ? ' is-active' : ''}`}
                disabled={action.disabled}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => {
                  if (action.disabled) return
                  onAction?.(action.id, item)
                  onClose?.()
                }}
              >
                <img src={action.icon} alt="" className="glass-menu-icon" />
                <span className="glass-menu-label">{action.label}</span>
                <span className="glass-menu-trail" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  )
}
